<template>
	<Row :gutter="16">
		<Col :span="24" :xl="{ span: 16 }">
			<TypographyTitle :level="3">{{ $t('settings.ftp-navigator.header') }}</TypographyTitle>
			<VerticalLayout>
				<div class="flex-wrapper">
					<Button type="default" @click="getList('..')">
						<ArrowUpOutlined /> {{ $t('settings.ftp-navigator.up') }}
					</Button>
					<div class="current-path nobr">{{ $t('settings.ftp-navigator.currentPath') }}</div>
					<div class="current-path-display nobr">{{ currentPathDisplay }}</div>
					<Button type="default" @click="getList('')">
						<RedoOutlined /> {{ $t('settings.ftp-navigator.refresh') }}
					</Button>
					<Button :type="selectedRowKeys.length > 0 ? 'primary' : 'default'" @click="onDownloadSelected" :disabled="selectedRowKeys.length === 0">
						<DownloadOutlined /> {{ $t('settings.ftp-navigator.downloadSelected', selectedRowKeys.length) }}
					</Button>
				</div>
				<div class="scrollable-x">
					<Table
						:dataSource="filteredItems"
						:loading="isLoading"
						:columns="columns"
						size="small"
						:pagination="{ defaultPageSize: 20, position: ['topRight', 'bottomRight'] }"
						:row-selection="{
							selectedRowKeys: selectedRowKeys,
							onChange: onSelect,
							getCheckboxProps: record => ({
								disabled: record.is_dir,
								name: record.name,
							}),
						}"
					>
						<template #headerCell="{ column }">
							<template v-if="column.dataIndex === 'name'">
								<div class="name-column-header-wrapper">
									<div class="name-column-header-title">{{ $t('settings.ftp-navigator.tableColumns.name') }}</div>
									<Input v-model:value="nameFilter" class="flex-grow" allowClear />
								</div>
							</template>
						</template>
						<template #bodyCell="{ column, record }">
							<template v-if="column.dataIndex === 'name'">
								<div v-if="record.is_dir" @click="getList(record.name)" class="dir-item">
									<FolderTwoTone twoToneColor="#c39738" /> {{ record.name }}
								</div>
								<FTPNavigatorFileItem v-else :record="record" :getFile="getFile"/>
							</template>
							<template v-else-if="column.dataIndex === 'modified'">
								<DateTimeDisplay :date="record.modified" />
							</template>
							<template v-else-if="column.dataIndex === 'size'">
								<span v-if="record.is_dir"></span>
							</template>
						</template>
					</Table>
				</div>
			</VerticalLayout>
		</Col>
		<Col :span="24" :xl="{ span: 8 }">
			<VerticalLayout>
				<TypographyTitle :level="3">{{ $t('settings.ftp-navigator.tasksHeader') }}</TypographyTitle>
				<VerticalLayout v-if="tasks.length > 0">
					<FTPNavigatorTask v-for="task in tasks" :key="task.id" :task="task" />
				</VerticalLayout>
				<Empty v-else :image="simpleImage" :description="$t('settings.ftp-navigator.messages.noTasks')" />
			</VerticalLayout>
		</Col>
	</Row>
</template>

<script>
import { ref, computed, onMounted } from 'vue';

import Api from '@/api/Api';
import Consts from '@/api/Consts';

import { useI18n } from 'vue-i18n'

import { TypographyTitle, Row, Col, Table, Button, Empty, Input, message } from 'ant-design-vue';
import { RedoOutlined, ArrowUpOutlined, FolderTwoTone, DownloadOutlined } from '@ant-design/icons-vue';

import DateTimeDisplay from '@/components/common/DateTimeDisplay';
import VerticalLayout from '@/components/common/VerticalLayout.vue';
import FTPNavigatorTask from '@/components/settings/ftpNavigator/FTPNavigatorTask.vue';
import FTPNavigatorFileItem from '@/components/settings/ftpNavigator/FTPNavigatorFileItem.vue';

import { showErrorMessage } from '@/components/common/errorMessage';

import { useInterval } from '@/utils/useInterval';


export default {
	components: {
		TypographyTitle, Row, Col, Table, Button, Empty, Input,
		DateTimeDisplay, VerticalLayout, FTPNavigatorTask, FTPNavigatorFileItem,
		FolderTwoTone, RedoOutlined, ArrowUpOutlined, DownloadOutlined,
	},
	setup() {
		const { t } = useI18n();
		const isLoading = ref(false);
		const currentPath = ref([]);
		const items = ref([]);
		const nameFilter = ref('');
		const filteredItems = computed(() => {
			const trimmedNameFilter = nameFilter.value.trim();
			if (trimmedNameFilter === '') {
				return items.value;
			}
			return items.value.filter(item => item.name.toLowerCase().includes(trimmedNameFilter.toLowerCase()));
		});
		const currentPathDisplay = computed(() => {
			return '/' + currentPath.value.join('/');
		});

		const getList = pathPart => {
			selectedRowKeys.value = [];
			nameFilter.value = '';
			isLoading.value = true;
			let newPath = [...currentPath.value];
			if (pathPart === '..') {
				newPath.pop();
			} else if (pathPart !== '') {
				newPath.push(pathPart);
			}
			Api.ftpGetList(newPath.join('/'), (data) => {
				if (data.status === 'ok') {
					items.value = data.files;
					currentPath.value = newPath;
				} else {
					showErrorMessage(data.errors);
				}
				isLoading.value = false;
			});
		}
		onMounted(() => {
			getList('');
		});

		const errorHandler = error => {
			if (error?.response?.status === 403) {
				message.error(t('settings.ftp-navigator.messages.noValidFiles'));
			} else {
				message.error(t('settings.ftp-navigator.messages.downloadError'));
			}
		}

		const getFile = (name, successHandler) => {
			let newPath = [...currentPath.value];
			newPath.push(name);
			Api.ftpGetFile(name, newPath.join('/'), successHandler, errorHandler);
		}

		const tasks = ref([]);
		const updateTasks = () => {
			Api.ftpGetTasks((data) => {
				tasks.value = data.tasks;
			});
		}
		useInterval(() => updateTasks(), 10000, true);

		const selectedRowKeys = ref([]);
		const onSelect = keys => selectedRowKeys.value = keys;
		const onDownloadSelected = () => {
			Api.ftpDownloadSelectedTaskStart(selectedRowKeys.value, currentPath.value.join('/'), data => {
				selectedRowKeys.value = [];
				if (data.status === 'ok') {
					message.success(t('settings.ftp-navigator.messages.downloadTaskStarted'));
					updateTasks();
				} else {
					showErrorMessage(data.errors);
				}
			});
		}

		return {
			isLoading,
			currentPathDisplay,
			filteredItems,
			nameFilter,
			columns: Consts.ftpNavigatorColumnsComp,
			getList,
			getFile,
			selectedRowKeys,
			onSelect,
			onDownloadSelected,
			tasks,
			simpleImage: Empty.PRESENTED_IMAGE_SIMPLE,
		};
	},
}
</script>

<style scoped>
.name-column-header-wrapper {
	display: flex;
	align-items: baseline;
}

.name-column-header-title {
	margin-inline-end: 8px;
}

.current-path {
	background-color: rgb(250, 250, 250);
	border: 1px solid rgb(240, 240, 240);
	padding: 4px 8px;
}

.current-path-display {
	flex-grow: 1;
	padding: 4px 8px;
	border: 1px solid rgb(240, 240, 240);
}

.dir-item {
	cursor: pointer;
	transition: color 0.3s;
}
.dir-item:hover {
	color: var(--ant-primary-color, #1890ff);
}
</style>
